import { api } from '~/services'
import { useProjectStore } from './projects'
import { notifyFailure, notifySuccess } from '~/utils/notifications'
import { fileNetworkRequest, HNRV2 } from '~/helpers'
import { AxiosError, type AxiosPromise } from 'axios'
import {
  type TransferObjectsReportExportDTO,
  type QueriesGetReportSettingsResponse,
  type CommandsUpdateReportSettingsRequest,
  EnumerationsProportionalAnalyses,
  type QueriesGetSummariesExportSettingsResponse,
  type CommandsUpdateSummariesExportSettingsRequest,
  EnumerationsSummaryExportSheet,
  type TransferObjectsSummariesDTO,
  EnumerationsAnalysisCategory,
} from 'client/sratool'
import {
  OReportBeingExported,
  useAAPExportReportsStore,
} from './AAPExportReports'
import { openDownloadFileDialogWithData } from '~/helpers/file'

interface ProjectReport extends TransferObjectsReportExportDTO {
  reportName: string
  groupName: string
  checked: boolean
}

interface ProjectSummary extends TransferObjectsSummariesDTO {
  reportName: string
  groupName: string
  checked: boolean
}

type ProjectExportable = ProjectSummary | ProjectReport

export const useProjectReportsStore = defineStore('project-reports', () => {
  const ProjectStore = useProjectStore()
  const AAPExportReportsStore = useAAPExportReportsStore()

  const loading = ref({
    reports: false,
    export: false,
    settings: false,
  })

  const filter = ref<string>('')
  const reports = ref<ProjectExportable[]>([])
  const reportGroups = ref<Record<string, ProjectExportable[]>>({})
  const reportSettings = ref<QueriesGetReportSettingsResponse | undefined>()

  const selectedSummarySettings = ref<EnumerationsSummaryExportSheet[]>([])
  const summarySettings = ref<QueriesGetSummariesExportSettingsResponse[]>([])

  const filteredReportGroups = computed(() => {
    if (!filter.value) return reportGroups.value
    const result: Record<string, ProjectExportable[]> = {}
    for (const [key, reportsArr] of Object.entries(reportGroups.value)) {
      result[key] = reportsArr.filter((report) =>
        report.reportName?.toLowerCase().includes(filter.value.toLowerCase())
      )
    }
    return result
  })

  const checkedReports = computed<ProjectExportable[]>(() => {
    const result: ProjectExportable[] = []
    for (const reportsArr of Object.values(reportGroups.value)) {
      result.push(...reportsArr.filter((report) => report.checked))
    }
    return result
  })

  const checkAll = async (group: (typeof reportGroups.value)[number]) => {
    const reportsFiltered = reportGroups.value[group[0].groupName].filter(
      (item) => {
        return item.reportName.includes(filter.value)
      }
    )

    const allCurrentFilteredReportsChecked = reportsFiltered.filter(
      (rf) => rf.checked
    )
    const isAllCurrentFilteredReportsChecked =
      allCurrentFilteredReportsChecked.length === reportsFiltered.length
    if (isAllCurrentFilteredReportsChecked) {
      reportGroups.value[group[0].groupName] = reportGroups.value[
        group[0].groupName
      ].map((r) => ({
        ...r,
        // uncheck filtered, keep unfiltered as checked states
        checked: allCurrentFilteredReportsChecked.some(
          (ac) => ac.reportName === r.reportName
        )
          ? false
          : r.checked,
      }))
    } else {
      reportGroups.value[group[0].groupName] = reportGroups.value[
        group[0].groupName
      ].map((r) => ({
        ...r,
        checked: reportsFiltered.some((rf) => rf.reportName === r.reportName)
          ? true
          : r.checked,
      }))
    }
  }

  const setReportGroups = () => {
    reportGroups.value = reports.value.reduce((groups, report) => {
      const category = report.groupName || 'Uncategorized'
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push({ ...report, checked: false })
      return groups
    }, {} as Record<string, ProjectReport[]>)
  }

  const getProjectReports = async (projectID: string) => {
    loading.value.reports = true
    reports.value = []

    const response = await HNRV2({
      fn: () =>
        api.workforceAnalyticsReports.apiSraReportsProjectIDGet(projectID),
    })
    loading.value.reports = false

    if (response instanceof AxiosError) {
      notifyFailure('Failed to fetch reports')
      return
    }

    reports.value = response.map((report) => {
      return {
        ...report,
        checked: false,
      }
    })
    setReportGroups()
  }

  const getProjectSummaries = async (projectID: string) => {
    loading.value.reports = true
    reports.value = []

    const response = await HNRV2({
      fn: () =>
        api.workforceAnalyticsSummaries.apiSraSummariesProjectIDGet(projectID),
    })
    loading.value.reports = false

    if (response instanceof AxiosError) {
      notifyFailure('Failed to fetch summaries')
      return
    }

    reports.value = response.map((summary) => {
      return {
        ...summary,
        reportName: summary.displayName,
        checked: false,
      }
    })
    setReportGroups()
  }

  const initiateExport = async ({
    endpoint,
    variant,
  }: {
    endpoint: AxiosPromise<string>
    variant: 'reports' | 'summaries'
  }) => {
    loading.value.export = true
    const response = await HNRV2({
      fn: () => endpoint,
    })

    if (response instanceof AxiosError) {
      notifyFailure(
        `Failed to export ${variant === 'reports' ? 'reports' : 'summaries'}`
      )
      loading.value.export = false
      return
    }

    AAPExportReportsStore.reportBeingExportedType =
      variant === 'reports'
        ? OReportBeingExported.PROJECT_REPORT
        : OReportBeingExported.PROJECT_SUMMARY
    AAPExportReportsStore.reportBeingExported = { notificationID: response }
    AAPExportReportsStore.exportDialogOpen = true
  }

  const exportReports = async () => {
    const selectedProjectID = ProjectStore.selectedProject?.projectID
    if (!selectedProjectID) {
      notifyFailure('No project selected')

      return
    }

    await initiateExport({
      endpoint: api.workforceAnalyticsReports.apiSraReportsProjectIDExportPost(
        selectedProjectID,
        checkedReports.value as TransferObjectsReportExportDTO[]
      ),
      variant: 'reports',
    })
  }

  const exportSummaries = async () => {
    const selectedProjectID = ProjectStore.selectedProject?.projectID
    if (!selectedProjectID) {
      notifyFailure('No project selected')

      return
    }

    await initiateExport({
      endpoint: api.workforceAnalyticsSummaries.apiSraSummariesExportPost({
        summaries: checkedReports.value.map((summary) => {
          return {
            analysisID: summary.analysisID,
            projectID: selectedProjectID,
            proportionalAnalysisID: summary.proportionalAnalysisID,
            strataLevelFieldGUID:
              'analysisFieldGUID' in summary ? summary.analysisFieldGUID : '',
          }
        }),
      }),
      variant: 'summaries',
    })
  }

  const cancelReport = async (reportNotificationID: string) => {
    const response = await HNRV2({
      fn: () =>
        api.workforceAnalyticsReportsFile.apiSraReportsNotificationIDCancelExportPost(
          reportNotificationID
        ),
    })

    if (response instanceof AxiosError) {
      notifyFailure('Problem cancelling report export')
      return
    }

    AAPExportReportsStore.clearExportData()

    notifySuccess('Your export was cancelled')
    loading.value.export = false
  }

  const downloadReport = async (notificationID: string) => {
    const response = await fileNetworkRequest({
      fn: () =>
        api.workforceAnalyticsReportsFile.apiSraReportsNotificationIDDownloadGet(
          notificationID
        ),
    })
    if (response instanceof AxiosError) {
      notifyFailure('Problem downloading report')
      return
    }

    openDownloadFileDialogWithData({
      response,
    })
  }

  const getReportSettings = async ({
    analysisID,
    analysisCategory,
  }: {
    analysisID: string
    analysisCategory: EnumerationsAnalysisCategory
  }) => {
    loading.value.settings = true
    const response = await HNRV2({
      fn: () =>
        api.workforceAnalyticsReports.apiSraReportsSettingsAnalysisIDAnalysisCategoryIDGet(
          analysisID,
          analysisCategory
        ),
    })

    loading.value.settings = false

    if (response instanceof AxiosError) {
      notifyFailure('Failed to fetch report settings')
      return
    }

    reportSettings.value = response
  }

  const setReportSettings = async (
    commandsUpdateReportSettingsRequest: CommandsUpdateReportSettingsRequest
  ) => {
    loading.value.settings = true
    const response = await HNRV2({
      fn: () =>
        api.workforceAnalyticsReports.apiSraReportsSettingsPut(
          commandsUpdateReportSettingsRequest
        ),
    })

    loading.value.settings = false

    if (response instanceof AxiosError) {
      notifyFailure('Failed to update report settings')
      return false
    }

    return true
  }

  const getSummarySettings = async ({
    analysisID,
    proportionalAnalysisType,
  }: {
    analysisID: string
    proportionalAnalysisType?: EnumerationsProportionalAnalyses
  }) => {
    loading.value.settings = true
    const response = await HNRV2({
      fn: () =>
        api.workforceAnalyticsSummaries.apiSraSummariesSettingsAnalysisIDGet(
          analysisID,
          proportionalAnalysisType
        ),
    })
    loading.value.settings = false
    if (response instanceof AxiosError) {
      notifyFailure('Failed to fetch summary settings')
      return
    }

    summarySettings.value = response
    selectedSummarySettings.value = response.reduce((acc, setting) => {
      if (setting.selected) acc.push(setting.summaryExportSheetID)
      return acc
    }, [] as EnumerationsSummaryExportSheet[])
  }

  const setSummarySettings = async (
    commandsUpdateSummariesExportSettingsRequest: CommandsUpdateSummariesExportSettingsRequest
  ) => {
    loading.value.settings = true
    const response = await HNRV2({
      fn: () =>
        api.workforceAnalyticsSummaries.apiSraSummariesSettingsPut(
          commandsUpdateSummariesExportSettingsRequest
        ),
    })
    loading.value.settings = false
    if (response instanceof AxiosError) {
      notifyFailure('Failed to update summary settings')
      return false
    }
    return true
  }

  return {
    loading,

    filter,

    reports,
    reportGroups,
    reportSettings,

    filteredReportGroups,
    checkedReports,
    checkAll,

    getProjectReports,
    exportReports,
    cancelReport,
    downloadReport,

    getProjectSummaries,
    exportSummaries,

    getReportSettings,
    setReportSettings,

    summarySettings,
    selectedSummarySettings,

    getSummarySettings,
    setSummarySettings,
  }
})
